import { ConversationDetailSchema, ConversationSummarySchema, DocumentDetailSchema, DocumentPageSchema, DocumentPatchSchema, DocumentStatsSchema, DocumentSummarySchema, NotePageSchema, IngestRequestSchema, IngestResponseSchema, QueryRequestSchema, QueryResponseSchema, RoomCreateSchema, RoomPatchSchema, RoomSchema, RoomOverviewSchema, GraphResponseSchema, UserProfileSchema, } from "@mimir/types";
import { z } from "zod";
export class MimirApiError extends Error {
    status;
    constructor(status, message) {
        super(message);
        this.status = status;
        this.name = "MimirApiError";
    }
}
export class MimirClient {
    config;
    constructor(config) {
        this.config = config;
    }
    async authHeaders() {
        const token = await this.config.getToken();
        if (!token) {
            throw new MimirApiError(401, "No auth token available");
        }
        return {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        };
    }
    async request(path, body, parser, signal) {
        const headers = await this.authHeaders();
        const res = await fetch(`${this.config.baseUrl}${path}`, {
            method: "POST",
            headers,
            body: JSON.stringify(body),
            signal,
        });
        if (!res.ok) {
            const detail = await res.text().catch(() => res.statusText);
            throw new MimirApiError(res.status, detail);
        }
        const json = await res.json();
        return parser.parse(json);
    }
    async get(path, parser) {
        const headers = await this.authHeaders();
        const res = await fetch(`${this.config.baseUrl}${path}`, { headers });
        if (!res.ok) {
            const detail = await res.text().catch(() => res.statusText);
            throw new MimirApiError(res.status, detail);
        }
        const json = await res.json();
        return parser.parse(json);
    }
    async delete(path) {
        const headers = await this.authHeaders();
        const res = await fetch(`${this.config.baseUrl}${path}`, {
            method: "DELETE",
            headers,
        });
        if (!res.ok) {
            const detail = await res.text().catch(() => res.statusText);
            throw new MimirApiError(res.status, detail);
        }
    }
    /* Ingest */
    async ingest(input) {
        const validated = IngestRequestSchema.parse(input);
        return this.request("/ingest", validated, IngestResponseSchema);
    }
    /* Capture */
    async captureText(input) {
        const headers = await this.authHeaders();
        delete headers["Content-Type"]; // let browser set multipart boundary
        const form = new FormData();
        form.set("content", input.content);
        if (input.title)
            form.set("title", input.title);
        if (input.user_tags?.length)
            form.set("user_tags", input.user_tags.join(","));
        if (input.room_id)
            form.set("room_id", input.room_id);
        const res = await fetch(`${this.config.baseUrl}/capture/text`, { method: "POST", headers, body: form });
        if (!res.ok)
            throw new MimirApiError(res.status, await res.text().catch(() => res.statusText));
        return IngestResponseSchema.parse(await res.json());
    }
    async captureNote(input) {
        const headers = await this.authHeaders();
        delete headers["Content-Type"];
        const form = new FormData();
        form.set("content", input.content);
        if (input.title)
            form.set("title", input.title);
        if (input.user_tags?.length)
            form.set("user_tags", input.user_tags.join(","));
        if (input.color)
            form.set("color", input.color);
        if (input.background_image)
            form.set("background_image", input.background_image);
        const res = await fetch(`${this.config.baseUrl}/capture/note`, { method: "POST", headers, body: form });
        if (!res.ok)
            throw new MimirApiError(res.status, await res.text().catch(() => res.statusText));
        return IngestResponseSchema.parse(await res.json());
    }
    async checkUrl(url) {
        return this.get(`/capture/preflight?url=${encodeURIComponent(url)}`, {
            parse: (d) => d,
        });
    }
    async captureUrl(input) {
        const headers = await this.authHeaders();
        delete headers["Content-Type"];
        const form = new FormData();
        form.set("url", input.url);
        if (input.title)
            form.set("title", input.title);
        if (input.user_tags?.length)
            form.set("user_tags", input.user_tags.join(","));
        if (input.room_id)
            form.set("room_id", input.room_id);
        const res = await fetch(`${this.config.baseUrl}/capture/url`, { method: "POST", headers, body: form });
        if (!res.ok)
            throw new MimirApiError(res.status, await res.text().catch(() => res.statusText));
        return IngestResponseSchema.parse(await res.json());
    }
    async captureFile(input) {
        const headers = await this.authHeaders();
        delete headers["Content-Type"];
        const form = new FormData();
        form.set("file", input.file);
        if (input.title)
            form.set("title", input.title);
        if (input.user_tags?.length)
            form.set("user_tags", input.user_tags.join(","));
        if (input.room_id)
            form.set("room_id", input.room_id);
        const res = await fetch(`${this.config.baseUrl}/capture/file`, { method: "POST", headers, body: form });
        if (!res.ok)
            throw new MimirApiError(res.status, await res.text().catch(() => res.statusText));
        return IngestResponseSchema.parse(await res.json());
    }
    /* Query */
    async query(input, signal) {
        const validated = QueryRequestSchema.parse(input);
        return this.request("/query", validated, QueryResponseSchema, signal);
    }
    async *queryStream(input, signal) {
        const validated = QueryRequestSchema.parse(input);
        const headers = await this.authHeaders();
        const res = await fetch(`${this.config.baseUrl}/query/stream`, {
            method: "POST",
            headers,
            body: JSON.stringify(validated),
            signal,
        });
        if (!res.ok) {
            const detail = await res.text().catch(() => res.statusText);
            throw new MimirApiError(res.status, detail);
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done)
                    break;
                buffer += decoder.decode(value, { stream: true });
                const parts = buffer.split("\n\n");
                buffer = parts.pop() ?? "";
                for (const part of parts) {
                    const line = part.trim();
                    if (!line.startsWith("data: "))
                        continue;
                    const data = line.slice(6);
                    if (data === "[DONE]")
                        return;
                    yield JSON.parse(data);
                }
            }
        }
        finally {
            reader.releaseLock();
        }
    }
    /* Conversations */
    async listConversations() {
        return this.get("/conversations", z.array(ConversationSummarySchema));
    }
    async getConversation(id) {
        return this.get(`/conversations/${id}`, ConversationDetailSchema);
    }
    async deleteConversation(id) {
        return this.delete(`/conversations/${id}`);
    }
    async truncateConversation(id, keepCount) {
        const headers = await this.authHeaders();
        const res = await fetch(`${this.config.baseUrl}/conversations/${id}/truncate`, {
            method: "POST",
            headers,
            body: JSON.stringify({ keep_count: keepCount }),
        });
        if (!res.ok) {
            const detail = await res.text().catch(() => res.statusText);
            throw new MimirApiError(res.status, detail);
        }
    }
    /* Documents */
    async listDocuments(params) {
        const qs = new URLSearchParams();
        if (params?.content_type)
            qs.set("content_type", params.content_type);
        if (params?.tag)
            qs.set("tag", params.tag);
        if (params?.room_id)
            qs.set("room_id", params.room_id);
        if (params?.unassigned)
            qs.set("unassigned", "true");
        if (params?.pinned)
            qs.set("pinned", "true");
        if (params?.limit)
            qs.set("limit", String(params.limit));
        if (params?.cursor)
            qs.set("cursor", params.cursor);
        const path = `/documents${qs.size ? `?${qs}` : ""}`;
        return this.get(path, DocumentPageSchema);
    }
    async getDocumentStats() {
        return this.get("/documents/stats", DocumentStatsSchema);
    }
    async getDocument(id) {
        return this.get(`/documents/${id}`, DocumentDetailSchema);
    }
    async patchDocument(id, patch) {
        const validated = DocumentPatchSchema.parse(patch);
        const headers = await this.authHeaders();
        const res = await fetch(`${this.config.baseUrl}/documents/${id}`, {
            method: "PATCH",
            headers,
            body: JSON.stringify(validated),
        });
        if (!res.ok) {
            const detail = await res.text().catch(() => res.statusText);
            throw new MimirApiError(res.status, detail);
        }
        return DocumentDetailSchema.parse(await res.json());
    }
    async deleteDocument(id) {
        return this.delete(`/documents/${id}`);
    }
    /* Notes */
    async listNotes(params) {
        const qs = new URLSearchParams();
        if (params?.limit)
            qs.set("limit", String(params.limit));
        if (params?.cursor)
            qs.set("cursor", params.cursor);
        if (params?.is_pinned !== undefined)
            qs.set("is_pinned", String(params.is_pinned));
        const path = `/notes${qs.size ? `?${qs}` : ""}`;
        return this.get(path, NotePageSchema);
    }
    async reorderNotes(ids) {
        const headers = await this.authHeaders();
        await fetch(`${this.config.baseUrl}/notes/reorder`, {
            method: "PATCH",
            headers: { ...headers, "Content-Type": "application/json" },
            body: JSON.stringify({ ids }),
        });
    }
    /* User */
    async getMe() {
        return this.get("/users/me", UserProfileSchema);
    }
    /* Rooms */
    async listRooms() {
        return this.get("/rooms", z.array(RoomSchema));
    }
    async getRoomsOverview() {
        return this.get("/rooms/overview", z.array(RoomOverviewSchema));
    }
    async createRoom(input) {
        return this.request("/rooms", RoomCreateSchema.parse(input), RoomSchema);
    }
    async patchRoom(id, patch) {
        const validated = RoomPatchSchema.parse(patch);
        const headers = await this.authHeaders();
        const res = await fetch(`${this.config.baseUrl}/rooms/${id}`, {
            method: "PATCH",
            headers,
            body: JSON.stringify(validated),
        });
        if (!res.ok) {
            const detail = await res.text().catch(() => res.statusText);
            throw new MimirApiError(res.status, detail);
        }
        return RoomSchema.parse(await res.json());
    }
    async deleteRoom(id) {
        return this.delete(`/rooms/${id}`);
    }
    /* Graph */
    async getGraph() {
        return this.get("/documents/graph", GraphResponseSchema);
    }
    /* Tags */
    async listTags() {
        return this.get("/tags", z.array(z.string()));
    }
}
//# sourceMappingURL=client.js.map